/**
 * Kalshi cancels that Open Orders has not confirmed (`timed-out` in
 * `kalshiCancelLifecycle.ts`): still listed, or listed again, after a cancel or
 * an edit. Retryable, and NOT proof an order survived.
 *
 * Invariants: by default one short summary (how many orders need confirmation,
 * and to verify before retrying) and one Details control, never a per-order
 * list. Details opens ONE region with a fixed total height that scrolls
 * inside: each outcome with its count and full message once, then its orders
 * with market identity, terms and the exact order ID. Derived from shared state
 * every render, so a retry or an authoritative read clears it; no dismiss,
 * because dismissing must not clear lifecycle state. Reads every stalled cancel,
 * including rows a filter or a collapsed section hides. Display only.
 */
import { useId, useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown } from 'lucide-react';
import { useAccountStore } from '../stores/accountStore';
import { describeMarket } from '../lib/kalshiTicker';
import {
  KALSHI_CANCEL_RELISTED_MESSAGE,
  KALSHI_CANCEL_SYNC_TIMEOUT_MESSAGE,
  KALSHI_CANCEL_SYNC_TIMEOUT_MS,
  KALSHI_EDIT_RELISTED_MESSAGE,
  type KalshiCancelStates,
} from '../lib/trading/kalshiCancelLifecycle';
import { sideNativePriceCents } from './cards/orderCardModel';
import { fmtCents } from './cards/cardFormat';
import type { KalshiOrder } from '../types/kalshi';

/** Short outcome labels, in display order. The full lifecycle message is shown
 *  once per outcome in Details; an unrecognized message is its own label. */
const OUTCOMES: readonly (readonly [message: string, label: string])[] = [
  [KALSHI_CANCEL_SYNC_TIMEOUT_MESSAGE, `still listed ${KALSHI_CANCEL_SYNC_TIMEOUT_MS / 1_000}s after cancel`],
  [KALSHI_CANCEL_RELISTED_MESSAGE, 'listed again after a confirmed cancel'],
  [KALSHI_EDIT_RELISTED_MESSAGE, 'original listed again after edit'],
];

export interface CancelSyncGroup {
  message: string;
  label: string;
  /** Newest first. */
  orderIds: string[];
}

/** Pure: the timed-out entries grouped by outcome message (known outcomes
 *  first, in lifecycle order), newest first within a group. */
export function groupTimedOutCancels(states: KalshiCancelStates): CancelSyncGroup[] {
  const byMessage = new Map<string, { startedAtMs: number; orderId: string }[]>();
  for (const [orderId, state] of Object.entries(states)) {
    if (state?.phase !== 'timed-out') continue;
    const message = state.message ?? KALSHI_CANCEL_SYNC_TIMEOUT_MESSAGE;
    const list = byMessage.get(message) ?? [];
    list.push({ startedAtMs: state.startedAtMs, orderId });
    byMessage.set(message, list);
  }
  const rank = (message: string) => {
    const i = OUTCOMES.findIndex(([m]) => m === message);
    return i === -1 ? OUTCOMES.length : i;
  };
  return [...byMessage.entries()]
    .sort(([a], [b]) => rank(a) - rank(b))
    .map(([message, entries]) => ({
      message,
      label: OUTCOMES.find(([m]) => m === message)?.[1] ?? message,
      orderIds: entries
        .sort((x, y) => y.startedAtMs - x.startedAtMs || (x.orderId < y.orderId ? -1 : 1))
        .map((e) => e.orderId),
    }));
}

function OrderLine({ orderId, order }: { orderId: string; order: KalshiOrder | undefined }) {
  // The canonical label (describeMarket): `betTeam` is the market's named YES
  // team, so a NO reads "(BAL) NO". A missing order still shows its exact ID
  // rather than an invented market.
  const described = order ? describeMarket(order.ticker, order.side) : null;
  const identity = order && described
    ? `${described.betTeam ? `(${described.betTeam}) ` : ''}${order.side.toUpperCase()} · ${described.marketLabel}`
    : 'Market not in the last Open Orders read';
  const terms = order
    ? `${order.action === 'sell' ? 'Sell' : 'Buy'} ${order.remainingCount} @ ${fmtCents(sideNativePriceCents(order))}`
    : null;
  return (
    <li className="py-1" data-testid="cancel-sync-order">
      <div className="text-foreground [overflow-wrap:anywhere]">{identity}</div>
      <div className="flex flex-wrap items-baseline gap-x-2 text-[10px] opacity-80">
        {terms && <span className="font-mono">{terms}</span>}
        <span className="select-all font-mono [overflow-wrap:anywhere]">{orderId}</span>
      </div>
    </li>
  );
}

type Variant = 'rail' | 'inline';

/**
 * `rail` sits under a rail card's scroll area (always in view); `inline` sits
 * in the Tracker's Open Orders section when the rail is not carrying it.
 */
export function KalshiCancelSyncNotice({ variant = 'rail', className = '' }: {
  variant?: Variant;
  className?: string;
}) {
  const states = useAccountStore((s) => s.kalshiCancelStates);
  const groups = useMemo(() => groupTimedOutCancels(states), [states]);
  // The open/closed state lives in the body, which unmounts once nothing is
  // stalled: a later, unrelated batch always starts collapsed.
  if (groups.length === 0) return null;
  return <NoticeBody groups={groups} variant={variant} className={className} />;
}

function NoticeBody({ groups, variant, className }: { groups: CancelSyncGroup[]; variant: Variant; className: string }) {
  const openOrders = useAccountStore((s) => s.openOrders);
  const orders = useMemo(() => new Map(openOrders.map((o) => [o.orderId, o])), [openOrders]);
  const [open, setOpen] = useState(false);
  const regionId = useId();
  const total = groups.reduce((sum, g) => sum + g.orderIds.length, 0);
  const subject = `${total} order${total === 1 ? ' needs' : 's need'} confirmation`;
  return (
    <div
      role="status"
      data-testid="kalshi-cancel-sync-notice"
      className={`rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-300 light:text-amber-800 ${className}`}
    >
      <div className="flex items-start gap-1.5">
        <AlertTriangle size={12} aria-hidden className="mt-0.5 shrink-0" />
        {/* The guidance moves to its own line as a unit on a narrow rail; it never splits mid-phrase. */}
        <p className="flex min-w-0 flex-1 flex-wrap gap-x-1.5">
          <span className="font-semibold">{subject}</span>
          <span className="whitespace-nowrap opacity-80">{' · Verify before retrying'}</span>
        </p>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={regionId}
          className="inline-flex shrink-0 items-center gap-0.5 rounded px-1 font-medium hover:underline focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400"
        >
          Details
          <ChevronDown size={11} aria-hidden className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {open && (
        <div
          id={regionId}
          // One bound for the whole Details region, however many outcomes or
          // orders: it scrolls inside, so the rail's orders and fills (or the
          // Tracker's order list) stay where they were. Focusable so the
          // keyboard can scroll it.
          className={`mt-1.5 overflow-y-auto border-t border-amber-500/20 pt-1 pr-1 ${variant === 'rail' ? 'max-h-40' : 'max-h-56'}`}
          tabIndex={0}
          role="region"
          aria-label={subject}
          data-testid="kalshi-cancel-sync-details"
        >
          {groups.map((g) => (
            <section key={g.message} className="py-1" aria-label={`${g.orderIds.length} ${g.label}`}>
              {/* Messages are lifecycle copy, but an unrecognized one is shown
                  as-is: a long token wraps inside the region, never sideways. */}
              <h4 className="font-semibold [overflow-wrap:anywhere]">
                <span className="font-mono">{g.orderIds.length}</span> {g.label}
              </h4>
              <p className="opacity-80 [overflow-wrap:anywhere]">{g.message}</p>
              <ul className="divide-y divide-amber-500/10">
                {g.orderIds.map((orderId) => (
                  <OrderLine key={orderId} orderId={orderId} order={orders.get(orderId)} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
